import useSWR from 'swr';
import { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { GridColDef } from '@mui/x-data-grid-pro';
import { SERVICES } from '@shared/constants/apiConfig';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { TableModal } from '@shared/components/modals/TableModal';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { ILenderListItem } from '@modules/admin/Tools/shared/LenderControlsHeader';
import { TextInputField } from '@shared/fields/TextInputField';
import { formatString, getOptions } from '@shared/commonFunctions';
import { getError } from '@shared/utils/helpers';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { colorTheme } from '@styles/muiTheme';
import { Wrapper } from '@styles/CommonStyles';
import { IMeridianLender, IRequestData } from './interfaces';
import { lenderAlreadyExists, requestDeleteMeridianLender, requestUpdateMeridianLender } from './requests';

const meridianLenderSchema = Yup.object().shape({
  lenderId: Yup.string().nullable().required(),
  userId: Yup.string().nullable().required(),
  password: Yup.string().nullable().required(),
  meridianDecisionLoanUrl: Yup.string().nullable().required(),
  meridianSubmitDocUrl: Yup.string().nullable().required(),
});

const newRecordValue = {
  lenderId: '',
  lenderName: '',
  userId: '',
  password: '',
  meridianDecisionLoanUrl: '',
  meridianSubmitDocUrl: '',
  isNewConfiguration: true,
};

const lenderAlreadyExistsError = 'Configuration already exists for this lender';

export const MeridianLenderConfiguration = () => {
  const { data: meridianLenders = [], mutate: meridianLendersMutate } = useSWR<IMeridianLender[]>(
    `${SERVICES.LENDER}/lenders/meridian-configuration`,
  );
  const { data: lenderList } = useSWR<ILenderListItem[]>(`/${SERVICES.LENDER}/lenders`);
  const lenderOptions = useMemo(() => getOptions(lenderList), [lenderList]);
  const [currentMeridianLender, setCurrentMeridianLender] = useState<IMeridianLender | null>(null);
  const [isSubmitting, setSubmittingStatus] = useState(false);
  const [customMessage, setCustomMessage] = useState('');

  const formConfig = useMemo(
    () => [
      {
        width: '50%',
        name: 'lenderId',
        label: 'Lender',
        options: lenderOptions,
        customMessage: {message: customMessage, color: colorTheme.palette.error.main},
        highlightFullBorder: customMessage ? true : false,
        onChange: () => {setCustomMessage('');},
        component: SelectAutocompleteField,
      },
      {
        width: '100%',
        name: 'userId',
        label: 'Meridian API UserId',
        component: TextInputField,
      },
      {
        width: '100%',
        name: 'password',
        label: 'Meridian API Password',
        type: 'password',
        component: TextInputField,
      },
      {
        width: '100%',
        name: 'meridianDecisionLoanUrl',
        label: 'Meridian Url',
        component: TextInputField,
      },
      {
        width: '100%',
        name: 'meridianSubmitDocUrl',
        label: 'Meridian Submit Doc Url',
        component: TextInputField,
      },
    ],
    [lenderOptions, customMessage]
  );

  const columns: GridColDef<IMeridianLender>[] = useMemo(
    () => [
      dataGridCol('lenderId', {
        flex: 0.5,
        minWidth: 60,
        headerName: 'Lender',
        valueFormatter: ({ value }) => formatString(lenderList?.find((lender) => lender.id === value)?.name || ''),
      }),
      dataGridCol('userId', {
        flex: 0.8,
        minWidth: 80,
        headerName: 'Api UserId',
      }),
      dataGridCol('meridianDecisionLoanUrl', {
        flex: 1,
        minWidth: 300,
        headerName: 'Meridian Url'
      }),
      dataGridCol('meridianSubmitDocUrl', {
        flex: 1,
        minWidth: 300,
        headerName: 'Meridian Submit Doc Url'
      }),
    ],
    [lenderList]
  );

  const gridActions = useMemo(
    () => ({
      onEdit: (data: IMeridianLender) => {
        setCustomMessage('');
        setCurrentMeridianLender({ ...data, isNewConfiguration: false });
      },
      onDelete: (data: IMeridianLender) => {
        requestDeleteMeridianLender(data?.lenderId);
      },
    }),
    []
  );

  const handleSubmit = useCallback(
    async (data: IMeridianLender) => {
      let requestData: IRequestData = {
        userId: data.userId,
        password: data.password,
        meridianDecisionLoanUrl: data.meridianDecisionLoanUrl,
        meridianSubmitDocUrl: data.meridianSubmitDocUrl,
        isNewConfiguration: data.isNewConfiguration ?? false,
      };
      if (!requestData.isNewConfiguration) {
        requestData = { ...requestData, oldLenderId: currentMeridianLender?.lenderId };
      }
      setSubmittingStatus(true);
      try {
        const response = await requestUpdateMeridianLender(requestData, data.lenderId);
        await meridianLendersMutate();
        if (response?.includes(lenderAlreadyExists)) {
          setCustomMessage(lenderAlreadyExistsError);
        } else {
          setCurrentMeridianLender(null);
          if (customMessage) {
            setCustomMessage('');
          }
        }
      } catch (error) {
        Notistack.generalError(getError(error));
      }
      setSubmittingStatus(false);
    },
    [currentMeridianLender, customMessage]
  );

  const handleCreate = useCallback(() => {
    setCustomMessage('');
    setCurrentMeridianLender(newRecordValue);
  },[]);

  const editingData = useMemo(
    () => ({
      lenderId: currentMeridianLender?.lenderId ?? '',
      lenderName: currentMeridianLender?.lenderName ?? '',
      userId: currentMeridianLender?.userId ?? null,
      password: currentMeridianLender?.password || null,
      meridianDecisionLoanUrl: currentMeridianLender?.meridianDecisionLoanUrl ?? null,
      meridianSubmitDocUrl: currentMeridianLender?.meridianSubmitDocUrl || null,
      isNewConfiguration: currentMeridianLender?.isNewConfiguration || false,
    }),
    [currentMeridianLender]
  );

  return (
    <Wrapper>
      <TableModal
        title='Meridian Lender Configuration'
        isOpen={!!currentMeridianLender}
        editingData={editingData}
        formConfig={formConfig}
        validationSchema={meridianLenderSchema}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onClose={() => setCurrentMeridianLender(null)}
      />

      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        loading={!meridianLenders}
        isRowSelectable={() => false}
        buttonTitle="Add New Record"
        name="Meridian Lender Configuration"
        columns={columns}
        actions={gridActions}
        rows={meridianLenders}
        getRowId={(row) => row.lenderId}
        onButtonClick={handleCreate}
      />
    </Wrapper>
  );
};
